/**
 * Job-report signature envelopes (Loop W2) — typed wrappers over the
 * W1 endpoints. Staff calls are authenticated; the `sign/{token}` calls
 * are public (the emailed link token is the credential).
 *
 *   GET  /job-reports/{id}                                  getJobReport
 *   GET  /job-reports/{id}/signature-envelope               getSignatureEnvelope
 *   POST /job-reports/{id}/signature-envelope               sendSignatureEnvelope
 *   POST /job-reports/{id}/signature-envelope/resend/{i}    resendSignatureLink
 *   POST /job-reports/{id}/signature-envelope/void          voidSignatureEnvelope
 *   GET  /job-reports/sign/{token}/meta                     getSignLinkMeta
 *   POST /job-reports/sign/{token}                          submitRemoteSignature
 *
 * Every function returns the unwrapped response data and rethrows the
 * raw Axios error so callers can read `err.response?.status/data`.
 */
import { apiClient } from './client';

export async function getJobReport(reportId) {
  const { data } = await apiClient.get(`/job-reports/${reportId}`);
  return data;
}

/** Latest envelope for the report (any status) → `{ envelope: {...} | null }`. */
export async function getSignatureEnvelope(reportId) {
  const { data } = await apiClient.get(`/job-reports/${reportId}/signature-envelope`);
  return data?.envelope || null;
}

/**
 * payload: { signers: [{field_key, name, email, order}], routing:
 * 'sequential'|'parallel', message, expires_in_days }
 */
export async function sendSignatureEnvelope(reportId, payload) {
  const { data } = await apiClient.post(`/job-reports/${reportId}/signature-envelope`, payload);
  return data;
}

/** Rotates the signer's link — the previously emailed link stops working. */
export async function resendSignatureLink(reportId, signerIndex) {
  const { data } = await apiClient.post(
    `/job-reports/${reportId}/signature-envelope/resend/${signerIndex}`, {},
  );
  return data;
}

export async function voidSignatureEnvelope(reportId, reason = '') {
  const { data } = await apiClient.post(
    `/job-reports/${reportId}/signature-envelope/void`, { reason },
  );
  return data;
}

export async function getSignLinkMeta(token) {
  const { data } = await apiClient.get(`/job-reports/sign/${encodeURIComponent(token)}/meta`);
  return data;
}

/** payload: { name, data_url, signature_type, accepted_acknowledgements: [id] } */
export async function submitRemoteSignature(token, payload) {
  const { data } = await apiClient.post(`/job-reports/sign/${encodeURIComponent(token)}`, payload);
  return data;
}

/** Absolute URL for the signer's read-only report view (iframe src). */
export function signLinkViewUrl(token) {
  return `${process.env.REACT_APP_BACKEND_URL}/api/job-reports/sign/${encodeURIComponent(token)}/view`;
}
